import { useEffect, useState } from 'react'
import {ArrowLeft, Eye, EyeOff, RotateCcw, Trash2} from 'lucide-react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { useAppAuth } from '@/contexts/AuthContext'
import { formatDateTimeBR, maskCPF, nowISO } from '@/utils/masks'
import { buildEscopoFilter, filtrarPorEscopo } from '@/utils/escopo'
import StatusBadge from '@/components/protocolos/StatusBadge'

interface Protocolo {
  _id: string
  numeroProtocolo: string
  nomeCompleto: string
  cpf: string
  motivo: string
  status: string
  cidadeId?: string
  excluidoPorNome?: string
  excluidoEm?: string
  createdAt: string
}

function ProtocolosExcluidos() {
  const { user } = useAppAuth()
  const [protocolos, setProtocolos] = useState<Protocolo[]>([])
  const [loading, setLoading] = useState(true)
  const [processando, setProcessando] = useState<string | null>(null)
  const [cpfsVisiveis, setCpfsVisiveis] = useState<string[]>([])

  const podeExcluirPermanente = user?.role === 'ADMIN'
  const podeVerCpf = user?.role === 'ADMIN'

  const alternarCpf = (id: string) => {
    setCpfsVisiveis(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      const filter: Record<string, any> = {
        excluido: true,
        ...buildEscopoFilter(user),
      }

      const r = await (lumi.entities as any).protocolos.list({
        filter,
        sort: { excluidoEm: -1 },
        limit: 200,
      })
      setProtocolos(filtrarPorEscopo(r?.list || [], user))
    }
    catch {
      setProtocolos([])
    }
    finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [user?._id, user?.role, user?.cidadeId, user?.secretariaId])

  const handleRestaurar = async (p: Protocolo) => {
    if (!window.confirm(`Restaurar o protocolo ${p.numeroProtocolo} para a listagem principal?`))
      return
    setProcessando(p._id)
    try {
      await (lumi.entities as any).protocolos.update(p._id, {
        excluido: false,
        updatedAt: nowISO(),
      })
      await (lumi.entities as any).protocolo_historico.create({
        protocoloId: p.numeroProtocolo,
        acao: 'RESTAURADO',
        descricao: `Requerimento restaurado por ${user?.nomeCompleto || 'utilizador'}.`,
        userId: user?.userId || '',
        creator: user?.userId || '',
        createdAt: nowISO(),
      })
      toast.success('Requerimento restaurado com sucesso.')
      fetchData()
    }
    catch {
      toast.error('Erro ao restaurar. Tente novamente.')
    }
    finally {
      setProcessando(null)
    }
  }

  const handleExcluirPermanente = async (p: Protocolo) => {
    if (!window.confirm(`Excluir PERMANENTEMENTE o protocolo ${p.numeroProtocolo}? Esta ação não pode ser desfeita.`))
      return
    setProcessando(p._id)
    try {
      await (lumi.entities as any).protocolos.delete(p._id)
      toast.success('Requerimento excluído permanentemente.')
      fetchData()
    }
    catch {
      toast.error('Erro ao excluir. Tente novamente.')
    }
    finally {
      setProcessando(null)
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <Link
          to="/protocolos"
          className="flex items-center gap-1.5 text-[13px] font-medium text-[#6B7280] hover:text-[#12141A]"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
          Voltar aos Requerimentos
        </Link>
      </div>

      <div>
        <h1 className="text-[18px] font-semibold text-[#12141A]">Requerimentos Excluídos</h1>
        <p className="text-[13px] text-[#6B7280] mt-0.5">
          {podeExcluirPermanente
            ? 'Como administrador, pode restaurar ou excluir permanentemente estes requerimentos.'
            : 'Visualização de requerimentos movidos para a área de excluídos. Apenas o administrador pode excluir permanentemente.'}
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-[#E7E9EC] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#E7E9EC] bg-[#FAFBFC]">
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">Nº Protocolo</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">Requerente</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">CPF</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">Estado</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">Excluído por</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">Excluído em</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F2F4]">
              {loading
                ? (
                    <tr>
                      <td colSpan={7} className="px-5 py-8 text-center text-[13px] text-[#9CA3AF]">A carregar...</td>
                    </tr>
                  )
                : protocolos.length === 0
                  ? (
                      <tr>
                        <td colSpan={7} className="px-5 py-8 text-center text-[13px] text-[#9CA3AF]">Nenhum requerimento excluído.</td>
                      </tr>
                    )
                  : protocolos.map(p => (
                      <tr key={p._id} className="hover:bg-[#F7F8FA] transition-colors">
                        <td className="px-5 py-3.5 text-[13.5px] font-medium text-[#12141A]">{p.numeroProtocolo}</td>
                        <td className="px-5 py-3.5 text-[13.5px] text-[#12141A] max-w-[200px] truncate">{p.nomeCompleto}</td>
                        <td className="px-5 py-3.5 text-[13.5px] text-[#4B5563]">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="tabular-nums">{cpfsVisiveis.includes(p._id) ? maskCPF(p.cpf) : '•••••'}</span>
                            {podeVerCpf && (
                              <button
                                type="button"
                                onClick={() => alternarCpf(p._id)}
                                className="text-[#9CA3AF] hover:text-[#1E4FBF] transition-colors"
                                title={cpfsVisiveis.includes(p._id) ? 'Ocultar CPF' : 'Mostrar CPF'}
                                aria-label={cpfsVisiveis.includes(p._id) ? 'Ocultar CPF' : 'Mostrar CPF'}
                              >
                                {cpfsVisiveis.includes(p._id)
                                  ? <EyeOff className="w-4 h-4" strokeWidth={1.9} />
                                  : <Eye className="w-4 h-4" strokeWidth={1.9} />}
                              </button>
                            )}
                          </span>
                        </td>
                        <td className="px-5 py-3.5"><StatusBadge status={p.status} comSelo /></td>
                        <td className="px-5 py-3.5 text-[13px] text-[#4B5563]">{p.excluidoPorNome || '-'}</td>
                        <td className="px-5 py-3.5 text-[13px] text-[#4B5563] whitespace-nowrap">{formatDateTimeBR(p.excluidoEm)}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              disabled={processando === p._id}
                              onClick={() => handleRestaurar(p)}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#EEF3FE] text-[#1E4FBF] text-[12.5px] font-medium hover:bg-[#DCE6FC] transition-colors disabled:opacity-60"
                            >
                              <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.75} />
                              Restaurar
                            </button>
                            {podeExcluirPermanente && (
                              <button
                                type="button"
                                disabled={processando === p._id}
                                onClick={() => handleExcluirPermanente(p)}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#FEF2F2] text-[#B91C1C] text-[12.5px] font-medium hover:bg-[#FEE2E2] transition-colors disabled:opacity-60"
                              >
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                                Excluir
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default ProtocolosExcluidos
